'use client';

/* Hallmark · genre: editorial · macrostructure: Narrative Workflow — R8 · theme: custom (warm ledger) · design-system: R8 contract */

/**
 * DifferentiatorsSection — R8 restructure §D (`.claude/handoffs/
 * 2026-08-12-r8-restructure-contract.md`). New section, slot 3 in the
 * locked order (Hero -> Differentiators -> OneProcess -> ...). Serif H2
 * "Not just a place to store leads." + two alternating copy/exhibit rows,
 * each exhibit a STATIC dark product scene inside `FigureFrame` mode A
 * (bounded hairline frame, R7 "dark exhibits only inside a framed figure"
 * law) — no card-in-card on the paper page chrome around it.
 *
 * Composition decisions:
 * - `RuleFlow`/`RuleFlowNode` were evaluated for D1 (contract: "reuse
 *   ui/RuleFlow primitives if they fit") but do NOT fit: `RuleFlow` is
 *   locked to exactly ONE action node, while `differentiators.d1.exhibit`
 *   carries FOUR action check-rows (assign team / owner / stage / priority)
 *   alongside three condition rows — a shape `RuleFlow`'s trigger-conditions-
 *   action contract cannot express. D1 instead hand-composes a static
 *   IF/THEN two-pane scene from the same primitives `RuleFlow`'s own nodes
 *   are built from (`MOCK_SURFACE_MINI`, `TONE`), matching the real
 *   two-pane rule-builder register `ProductStoryFigures.tsx`'s `RouteFigure`
 *   already established for this exact "conditions pane / actions pane"
 *   shape (bespoke there too, for the identical reason).
 * - D2's campaign cards use `Surface` (the house card recipe) rather than
 *   `DataRow` — `DataRow` requires an avatar slot, which has no counterpart
 *   in a campaign's label/value stat rows (contract: "via Surface-on-dark or
 *   DataRow stacks" — either is sanctioned; `DataRow`'s avatar contract does
 *   not fit here).
 * - Both exhibits render `FigureFrame disclosure="none"` + their own
 *   composed `IllustrativeLabel surface="paper"` (the exact pattern
 *   `HeroChatReveal.tsx` already reviewed/shipped) instead of FigureFrame's
 *   auto-rendered default: that default's `IllustrativeLabel` renders in the
 *   PAPER zone below the frame but keeps its `surface='dark'` default
 *   (`text-white/60`), which measured near-invisible on paper the one time
 *   this exact combination was live-reviewed (see `IllustrativeLabel.tsx`'s
 *   own file-header note). Composing it manually also lets the honesty tag
 *   read the bilingual `exhibit.tag` copy instead of FigureFrame's hardcoded
 *   RO-only default text.
 * - Binding styling rule for this round ("mono kickers via IllustrativeLabel"):
 *   the section eyebrow and each row's "DIFFERENTIATOR 0N" kicker reuse
 *   `IllustrativeLabel` (`surface="paper"`, un-absolutely-positioned via the
 *   same `static bottom-auto right-auto z-auto` override every other
 *   composed-elsewhere caller uses) rather than a hand-rolled pill.
 * - `content` passed to `FigureFrame` is a literal `<div data-mass-root>`
 *   wrapper written at the call site (never the extracted content
 *   components below directly) — `FilledFigureContent`'s own doc comment
 *   names this as the sanctioned seam for a composite whose own prop type
 *   isn't `{'data-mass-root': true}`.
 * - No amber anywhere in this file. The real law is TWO-PART, not the flat
 *   "amber is pole-only" this header used to state (corrected 2026-08-12,
 *   R8 review FIX 8 — the old wording contradicted the R7 contract line 20
 *   budget that OneProcessSection's step numerals and BrainSystemSection's
 *   Cosmos core already draw on, legally):
 *     (a) amber as FILL — CTA buttons, `Chip tone='accent'` washes, focus
 *         rings, atmosphere — IS pole-only: hero + final-CTA, plus the one
 *         named component-scale exception, the Pricing "Popular" badge;
 *     (b) amber as GLYPH, via `PAPER_ACCENT_INK` (the contrast-proven
 *         readable-amber-as-text token), is a BUDGETED accent — spent on
 *         named single marks, today OneProcess's four step numerals, the
 *         Brain core mark, and FAQ's open-question type — all inside the
 *         R7 contract's "≤5% of a viewport" ceiling.
 *   This section takes NEITHER: it is not a conversion pole (a) and claims
 *   no share of the glyph budget (b). Campaign B's "highest ROI" emphasis
 *   uses `TONE.success` (green) instead, the same positive/category-tone
 *   law `FigureFrame`'s own chapter-accent washes use.
 * - Every `TONE.*` / `statusTone` site below renders INSIDE a bounded dark
 *   `FigureFrame` exhibit interior, never on this section's own paper band —
 *   the one context those dark-verified recipes are legal in. Each carries
 *   the `@dark-tone-on-paper-ok` marker `scripts/check-landing-tone-surface.mjs`
 *   requires of a file that both paints paper and emits a dark-only tone
 *   (that check exists because `HeroConvergenceFigure.tsx` did exactly that
 *   on real paper and shipped a 2.01:1 chip).
 */

import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LANDING_CONTENT } from '../constants/content';
import LandingReveal from './LandingReveal';
import { useLocale, type BilingualContent } from '@/features/marketing/hooks/useLocale';
import { Chip } from '../ui/Chip';
import { FigureFrame } from '../ui/FigureFrame';
import { IllustrativeLabel } from '../ui/IllustrativeLabel';
import { Surface } from '../ui/Surface';
import { DARK_INK } from '../ui/dark-tokens';
import { MOCK_SURFACE_MINI } from '../ui/mock-surface-recipe';
import { PAPER_BAND, PAPER_INK, PAPER_RULE } from '../ui/paper-tokens';
import { TONE } from '../ui/tone';

type TFn = (c: BilingualContent) => string;

/** The bilingual, un-positioned honesty marker every filled exhibit here
 * carries below its `FigureFrame` — see the file header's composition note. */
function ExhibitDisclosure({ text }: { text: string }) {
  return (
    <IllustrativeLabel
      text={text}
      surface="paper"
      className="static bottom-auto right-auto z-auto ml-auto mt-2 flex w-fit"
    />
  );
}

/** D1's IF pane + THEN pane — the routing-rule configurator's real content,
 * factored out of the literal `data-mass-root` wrapper `FigureFrame`
 * requires at its own call site (file header note). */
function RoutingRuleContent({ t }: { t: TFn }) {
  const { exhibit } = LANDING_CONTENT.differentiators.d1;

  return (
    <>
      <div className={cn(MOCK_SURFACE_MINI, 'flex flex-1 flex-col gap-0.5 p-3')}>
        <span
          className={cn(
            'mb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em]',
            DARK_INK.tertiary.text
          )}
        >
          {t(exhibit.ifLabel)}
        </span>
        <div className="flex flex-col divide-y divide-[#FFFFFF0F]">
          {exhibit.conditions.map((cond, i) => (
            <div key={i} className="flex items-center justify-between gap-3 py-2">
              <span className={cn('text-[12px]', DARK_INK.secondary.text)}>{t(cond.label)}</span>
              <span
                className={cn(
                  'truncate rounded-md border border-[#FFFFFF1A] px-2 py-1 text-[11px] font-medium',
                  DARK_INK.bright.text
                )}
              >
                {t(cond.value)}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className={cn(MOCK_SURFACE_MINI, 'flex flex-1 flex-col gap-0.5 p-3')}>
        <span
          className={cn(
            'mb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em]',
            DARK_INK.tertiary.text
          )}
        >
          {t(exhibit.thenLabel)}
        </span>
        <div className="flex flex-col gap-2 py-1">
          {exhibit.actions.map((action, i) => (
            <div key={i} className="flex items-center gap-2">
              {/* @dark-tone-on-paper-ok reason=renders inside FigureFrame's dark MOCK_SURFACE_MINI exhibit interior, never on this section's paper band */}
              <Check
                aria-hidden="true"
                className={cn('size-3.5 shrink-0', TONE.success.iconTint)}
              />
              <span className={cn('text-[12px]', DARK_INK.bright.text)}>{t(action.label)}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

type Campaign = (typeof LANDING_CONTENT.differentiators.d2.exhibit)['campaignA'];

/** One campaign card — `Surface` (house card recipe) + a label/value row
 * stack, house `DARK_INK` ladder throughout. Campaign B's `highlight` swaps
 * the border to `TONE.success` (never amber — see file header). */
function CampaignCard({
  campaign,
  t,
  highlight,
}: {
  campaign: Campaign;
  t: TFn;
  highlight: boolean;
}) {
  return (
    // @dark-tone-on-paper-ok reason=Surface is the dark card recipe, always composed inside a FigureFrame dark exhibit interior
    <Surface className={cn('flex flex-1 flex-col gap-2 p-3', highlight && TONE.success.chipBorder)}>
      {/* 2026-08-13, RO live-UAT bug: this row used to be a bare
          items-center/justify-between flex with no wrap. The Chip is
          `whitespace-nowrap` and cannot shrink, so it claimed its full
          intrinsic width and the campaign NAME — the row's identity — ate the
          entire deficit: RO "Cele mai ieftine lead-uri" (25 chars vs EN's 14)
          crushed "Campania A" to clientWidth 40 / scrollWidth 76, rendering
          "Ca…". Adding `flex-wrap` + a row gap makes the badge drop to its own
          line instead, so BOTH strings stay fully readable at every width;
          when they fit (EN) the row is visually unchanged, badge still right-
          aligned by `justify-between`. This is the exact shape
          `HeroConvergenceFigure.tsx`'s source rows already use for the same
          name+chip anatomy (reuse before invent), and it works precisely
          because the title carries no `flex-1`/`basis-0`: a flex-basis:0 child
          always "fits", so a row containing one never wraps at all.
          `truncate` stays as the last-resort net for a single string wider
          than the whole card. Class-killed by
          `scripts/check-truncation-priority-inversion.mjs`. */}
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5">
        <span className={cn('truncate text-[13px] font-semibold', DARK_INK.bright.text)}>
          {t(campaign.name)}
        </span>
        {/* @dark-tone-on-paper-ok reason=chip sits on the dark Surface above, inside FigureFrame's exhibit interior, never on paper */}
        <Chip
          variant="status"
          label={t(campaign.tag)}
          statusTone={highlight ? 'success' : 'neutral'}
        />
      </div>
      <div className="flex flex-col divide-y divide-[#FFFFFF0F]">
        {campaign.rows.map((row, i) => (
          <div key={i} className="flex items-center justify-between gap-3 py-1.5">
            <span className={cn('text-[11px]', DARK_INK.tertiary.text)}>{t(row.label)}</span>
            <span className={cn('text-[12px] font-medium tabular-nums', DARK_INK.bright.text)}>
              {t(row.value)}
            </span>
          </div>
        ))}
      </div>
    </Surface>
  );
}

/** D2's two campaign cards + insight footer row. */
function AttributionContent({ t }: { t: TFn }) {
  const { exhibit } = LANDING_CONTENT.differentiators.d2;

  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <CampaignCard campaign={exhibit.campaignA} t={t} highlight={false} />
        <CampaignCard campaign={exhibit.campaignB} t={t} highlight />
      </div>
      <p
        className={cn(
          'border-t border-[#FFFFFF12] pt-3 text-[12px] leading-[1.5]',
          DARK_INK.secondary.text
        )}
      >
        {t(exhibit.insight)}
      </p>
    </>
  );
}

/** The shared eyebrow/kicker treatment (file header: "mono kickers via
 * IllustrativeLabel"). */
function Kicker({ text, className }: { text: string; className?: string }) {
  return (
    <IllustrativeLabel
      text={text}
      surface="paper"
      className={cn('static bottom-auto right-auto z-auto flex w-fit', className)}
    />
  );
}

function DifferentiatorsSectionInner() {
  const { differentiators } = LANDING_CONTENT;
  const { d1, d2 } = differentiators;
  const { t } = useLocale();

  return (
    <section id="differentiators" className="relative py-20 lg:py-24 overflow-hidden scroll-mt-24">
      {/* R8 band ladder proposal: Hero base -> Diff alt. */}
      <div className={cn('absolute inset-0', PAPER_BAND.alt.bg)} aria-hidden="true" />

      <div className="relative z-10 max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <LandingReveal>
            <div className="max-w-[52ch] mb-12 lg:mb-16">
              <Kicker text={t(differentiators.eyebrow)} className="mb-5" />
              <h2
                className={cn(
                  'landing-serif-display mb-5 max-w-[20ch] text-[34px] leading-[1.15] lg:mb-6 lg:text-[40px] lg:leading-[1.1]',
                  PAPER_INK.primary.text
                )}
              >
                {t(differentiators.h2)}
              </h2>
              <p
                className={cn(
                  'text-lg lg:text-xl leading-[1.55] tracking-[-0.01em]',
                  PAPER_INK.secondary.text
                )}
              >
                {t(differentiators.sub)}
              </p>
            </div>
          </LandingReveal>

          {/* Row 1 — copy LEFT, exhibit RIGHT at lg; mobile stacks copy first
              (natural DOM order, no `order-*` needed on this row). */}
          <LandingReveal>
            <div className="grid grid-cols-1 gap-8 mb-16 lg:mb-20 lg:grid-cols-2 lg:items-center lg:gap-14">
              <div>
                <Kicker text={t(d1.kicker)} className="mb-4" />
                <h3
                  className={cn(
                    'landing-serif-display mb-4 max-w-[16ch] text-[28px] leading-[1.15] lg:text-[32px] lg:leading-[1.1]',
                    PAPER_INK.primary.text
                  )}
                >
                  {t(d1.h3)}
                </h3>
                <p className={cn('text-[16px] leading-[1.6]', PAPER_INK.secondary.text)}>
                  {t(d1.body)}
                </p>
              </div>
              <div>
                <FigureFrame
                  mode="A"
                  disclosure="none"
                  caption={t(d1.exhibit.title)}
                  content={
                    <div
                      data-mass-root
                      className="flex w-full flex-col gap-3 p-4 md:flex-row md:gap-4 md:p-5"
                    >
                      <RoutingRuleContent t={t} />
                    </div>
                  }
                />
                <ExhibitDisclosure text={t(d1.exhibit.tag)} />
              </div>
            </div>
          </LandingReveal>

          {/* Row 2 — exhibit LEFT, copy RIGHT at lg (alternation); mobile
              stacks copy first via `order-*` (DOM keeps copy before exhibit
              at every width, so the un-styled/no-JS reading order is always
              copy-then-exhibit — only the lg visual order flips). */}
          <LandingReveal delay={80}>
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center lg:gap-14">
              <div className="lg:order-2">
                <Kicker text={t(d2.kicker)} className="mb-4" />
                <h3
                  className={cn(
                    'landing-serif-display mb-4 max-w-[18ch] text-[28px] leading-[1.15] lg:text-[32px] lg:leading-[1.1]',
                    PAPER_INK.primary.text
                  )}
                >
                  {t(d2.h3)}
                </h3>
                <p className={cn('mb-4 text-[16px] leading-[1.6]', PAPER_INK.secondary.text)}>
                  {t(d2.body)}
                </p>
                <p
                  className={cn(
                    'border-l-2 pl-4 text-[15px] font-medium leading-[1.5]',
                    PAPER_RULE.hairline.border,
                    PAPER_INK.primary.text
                  )}
                >
                  {t(d2.callout)}
                </p>
              </div>
              <div className="lg:order-1">
                <FigureFrame
                  mode="A"
                  disclosure="none"
                  caption={t(d2.exhibit.title)}
                  content={
                    <div data-mass-root className="flex w-full flex-col gap-4 p-4 md:p-5">
                      <AttributionContent t={t} />
                    </div>
                  }
                />
                <ExhibitDisclosure text={t(d2.exhibit.tag)} />
              </div>
            </div>
          </LandingReveal>
        </div>
      </div>
    </section>
  );
}

export default function DifferentiatorsSection() {
  return <DifferentiatorsSectionInner />;
}
